import React from 'react';
import { useInView } from 'react-intersection-observer';
import { purchasePath, isWebApp } from '../seo/site';
import SectionGridBackground from './SectionGridBackground';
import './ProductSection.css';

const ProductSection = ({ product, animateOnEnter = true }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
    skip: !animateOnEnter,
  });

  const { id, title, accentColor, app, features, whyUse, externalUrl } = product;
  const isWeb = isWebApp(product);
  const launchUrl = externalUrl || app.appStoreUrl;
  const isAnimated = animateOnEnter && inView;

  return (
    <section
      id={id}
      className="product-section section"
      style={{
        '--accent': accentColor || '#007AFF',
        '--accent-glow': product.accentGlowColor || accentColor || '#007AFF',
        '--accent-glow-opacity': product.accentGlowOpacity ?? 0.21,
        '--accent-glow-width': product.accentGlowWidth || 'min(132vw, 1700px)',
        '--accent-glow-height': product.accentGlowHeight || 'min(125vh, 1160px)',
        '--accent-glow-blur': product.accentGlowBlur || '101px',
      }}
    >
      <SectionGridBackground />
      <div className="product-section-inner">
        <div className={`product-hero ${isAnimated ? 'animate' : ''}`} ref={animateOnEnter ? ref : null}>
          <div className="product-hero-main">
            <a
              href={launchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="product-icon-link"
            >
              <img
                src={app.image}
                alt={app.imageAlt}
                className="product-icon"
                width={180}
                height={180}
                loading={animateOnEnter ? 'lazy' : 'eager'}
                decoding="async"
              />
            </a>

            <div className="product-hero-text">
              <p className="product-name">{title}</p>
              <h2 className="product-headline">{app.title}</h2>
              <p className="product-tagline">{app.tagline}</p>
              <p className="product-description">{app.description}</p>

              <div className="product-meta">
                {isWeb ? (
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="web-app-launch-btn"
                  >
                    Open {title}
                  </a>
                ) : (
                  <>
                    <a
                      href={app.appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-store-badge-link"
                    >
                      <img
                        src="/images/mac-app-store-badge.svg"
                        alt="Download on the Mac App Store"
                        className="app-store-badge"
                        height={40}
                        decoding="async"
                      />
                    </a>
                    {product.directDownload?.enabled && (
                      <a
                        href={purchasePath(id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="direct-download-badge-link"
                        aria-label={`Direct download ${title}`}
                      >
                        <img
                          src={product.directDownload.badgeImage || '/images/direct-download-badge.svg'}
                          alt="Direct Download"
                          className="direct-download-badge"
                          height={40}
                          decoding="async"
                        />
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {app.screenshot && (
            <div className="product-screenshot-wrap">
              <img
                src={app.screenshot}
                alt={app.screenshotAlt}
                className="product-screenshot"
                width={1280}
                height={800}
                loading="lazy"
                decoding="async"
              />
            </div>
          )}
        </div>

        <div className={`product-details ${isAnimated ? 'animate' : ''}`}>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="why-use">
            <p className="why-tagline">{whyUse.tagline}</p>
            <p>{whyUse.description}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
